<template>
  <template v-if="shouldShow">
    <div
      v-if="variant === 'sidebar'"
      class="rounded-lg border border-blue/30 bg-[#EEF5FF] p-3 text-xs leading-snug"
    >
      <p
        class="font-bold text-blue text-[11px] uppercase tracking-wide mb-1 flex items-center gap-1.5"
      >
        <CrownIcon class="size-3.5" />
        {{ eyebrow }}
      </p>
      <p class="text-gray-600 mb-2">
        <template v-if="bannerKind === 'trial'">
          <span class="font-semibold text-gray-800"
            >{{ daysLabel(trialDaysRemaining) }} left</span
          >
          - upgrade now to keep full access when your trial ends.
        </template>
        <template v-else-if="bannerKind === 'renewal'">
          <span class="font-semibold text-gray-800">
            {{ expiringSubscription.board_name }} ·
            {{ daysLabel(expiringSubscription.days_remaining) }} left
          </span>
          - renew now to keep all available features without a break.
        </template>
        <template v-else>
          Upgrade for full access to all available features.
        </template>
      </p>
      <button
        type="button"
        class="w-full text-center text-[11px] font-semibold text-blue hover:underline cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue/30 focus-visible:ring-offset-1 rounded-sm"
        @click="handleAction"
      >
        {{ actionLabel }} →
      </button>
    </div>

    <section
      v-else
      aria-labelledby="subscription-banner-title"
      class="relative overflow-hidden rounded-xl border border-[#C8DBFF] bg-[#F4F8FF] px-4 py-3"
    >
      <div
        class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-5"
      >
        <div class="flex min-w-0 items-start gap-3">
          <div
            class="flex size-9 shrink-0 items-center justify-center rounded-lg border border-blue/20 bg-white text-blue shadow-sm"
          >
            <CrownIcon class="size-[18px]" />
          </div>
          <div class="min-w-0">
            <div class="mb-1.5 flex flex-wrap items-center gap-2">
              <p
                class="text-[11px] font-bold uppercase tracking-[0.12em] text-blue"
              >
                {{ eyebrow }}
              </p>
              <span
                v-if="statusLabel"
                class="inline-flex items-center gap-1 rounded-full border border-blue/15 bg-white px-2 py-0.5 text-[11px] font-semibold text-[#1E3456]"
              >
                <Clock3Icon class="size-3 text-blue" />
                {{ statusLabel }}
              </span>
            </div>
            <h2
              id="subscription-banner-title"
              class="text-sm font-bold text-[#1E3456] sm:text-base"
            >
              {{ dashboardTitle }}
            </h2>
            <p class="mt-0.5 max-w-3xl text-xs leading-relaxed text-gray-600">
              {{ dashboardDescription }}
            </p>
          </div>
        </div>
        <Button
          variant="blue"
          class="min-h-9 w-full self-start px-4 focus-visible:ring-2 focus-visible:ring-blue/30 focus-visible:ring-offset-2 sm:w-auto sm:self-center"
          @click="handleAction"
        >
          <span>{{ actionLabel }}</span>
          <ArrowRightIcon class="ml-2 size-4" />
        </Button>
      </div>
    </section>
  </template>
</template>

<script setup>
import { ArrowRightIcon, Clock3Icon, CrownIcon } from 'lucide-vue-next';
import Button from '@/components/Button.vue';
import { useAuthStore } from '@/stores/auth';

defineProps({
  variant: {
    type: String,
    default: 'sidebar',
    validator: (value) => ['sidebar', 'dashboard'].includes(value),
  },
});

const router = useRouter();
const authStore = useAuthStore();
const {
  isSubscribed,
  isTrialActive,
  trialDaysRemaining,
  expiringSubscription,
} = storeToRefs(authStore);

const shouldShow = computed(
  () =>
    Boolean(authStore.user) &&
    (isTrialActive.value ||
      Boolean(expiringSubscription.value) ||
      !isSubscribed.value)
);

const bannerKind = computed(() => {
  if (isTrialActive.value) return 'trial';
  if (expiringSubscription.value) return 'renewal';
  return 'free';
});

const eyebrow = computed(() => {
  if (bannerKind.value === 'trial') return 'Free trial';
  if (bannerKind.value === 'renewal') return 'Renewal';
  return 'Free plan';
});

const daysLabel = (days) => `${days} day${days === 1 ? '' : 's'}`;

const statusLabel = computed(() => {
  if (bannerKind.value === 'trial') {
    return `${daysLabel(trialDaysRemaining.value)} left`;
  }
  if (bannerKind.value === 'renewal') {
    return `${expiringSubscription.value.board_name} · ${daysLabel(
      expiringSubscription.value.days_remaining
    )} left`;
  }
  return '';
});

const dashboardTitle = computed(() => {
  if (bannerKind.value === 'trial') return 'Keep your full study toolkit';
  if (bannerKind.value === 'renewal') {
    return `Keep your ${expiringSubscription.value.board_name} access uninterrupted`;
  }
  return 'Unlock your complete study toolkit';
});

const dashboardDescription = computed(() => {
  if (bannerKind.value === 'trial') {
    return 'Upgrade now to keep full access when your trial ends.';
  }
  if (bannerKind.value === 'renewal') {
    return 'Renew before your plan ends to keep your study flow uninterrupted.';
  }
  return 'Get recommended questions, exam builder, sorty AI, and insights in one plan.';
});

const actionLabel = computed(() =>
  bannerKind.value === 'renewal' ? 'Renew now' : 'Upgrade to Pro'
);

const handleAction = () => {
  const renewal = expiringSubscription.value;
  router.push({
    name: 'subscription-checkout',
    query:
      bannerKind.value === 'renewal'
        ? renewal.is_bundle
          ? { bundle: 'true' }
          : { board: renewal.board_uuid }
        : {},
  });
};
</script>
